import { useState } from "react";
import API, { authFetch } from "../../services/api";

function PlantDocumentsPanel({
  cardStyle,
  emptyBoxStyle,
  inputStyle,
  sectionTitleStyle,
  primaryButtonStyle,
  documentiImpianto,
  mostraNuovoDocumento,
  nuovoDocumento,
  setMostraNuovoDocumento,
  setNuovoDocumento,
  salvaNuovoDocumento,
  salvataggioDocumento,
  uploadProgressDocumento,
  onDocumentoEliminato,
  renderActions,
}) {
  const utenteCorrente = JSON.parse(localStorage.getItem("utenteCorrente"));
  const isViewer = utenteCorrente?.role === "viewer";

  const [previewDocumento, setPreviewDocumento] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [ricercaDocumenti, setRicercaDocumenti] = useState("");
  const [toast, setToast] = useState(null);
  const [ordinamentoDocumenti, setOrdinamentoDocumenti] = useState("recenti");
  const [ordineDocumenti, setOrdineDocumenti] = useState("desc");

  const labelStyle = {
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: "6px",
    color: "#475569",
  };

  const helpTextStyle = {
    fontSize: "12px",
    color: "#64748b",
    marginTop: "5px",
    lineHeight: "1.4",
  };

  const fieldWrapperStyle = {
    display: "flex",
    flexDirection: "column",
  };

  const getFileUrl = (filePath) => {
    if (!filePath) return null;
    if (filePath.startsWith("http")) return filePath;
    return `${API}/${filePath.replaceAll("\\", "/")}`;
  };

  const getFileLabel = (filePath) => {
    if (!filePath) return "—";
    const parts = filePath.replaceAll("\\", "/").split("/");
    return parts[parts.length - 1] || "Documento";
  };

  const getFileExtension = (filePath) => {
    if (!filePath) return "";
    return filePath.replaceAll("\\", "/").split(".").pop().toLowerCase();
  };

  const isImageFile = (filePath) =>
    ["jpg", "jpeg", "png", "gif", "webp"].includes(getFileExtension(filePath));

  const isPdfFile = (filePath) => getFileExtension(filePath) === "pdf";

  const getDocumentIcon = (filePath) => {
    const ext = getFileExtension(filePath);
    if (ext === "pdf") return "📕";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "🖼️";
    if (["doc", "docx"].includes(ext)) return "📘";
    return "📄";
  };

  const getFileTypeLabel = (filePath) => {
    const ext = getFileExtension(filePath);
    if (!ext) return "File";
    if (ext === "pdf") return "PDF";
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "Immagine";
    if (["doc", "docx"].includes(ext)) return "Documento Word";
    return ext.toUpperCase();
  };

  const getCategoryBadgeStyle = () => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: "999px",
    background: "#e0f2fe",
    color: "#075985",
    fontSize: "12px",
    fontWeight: 800,
  });

  const getFileTypeBadgeStyle = () => ({
    display: "inline-flex",
    alignItems: "center",
    padding: "5px 10px",
    borderRadius: "999px",
    background: "#f1f5f9",
    color: "#334155",
    fontSize: "12px",
    fontWeight: 800,
  });

  const mostraToast = (message, type = "success") => {
    setToast({ message, type });

    setTimeout(() => {
      setToast(null);
    }, 3000);
  };

  const gestisciFileDocumento = (selectedFile) => {
    if (!selectedFile) return;

    const allowedTypes = [
      "application/pdf",
      "image/jpeg",
      "image/png",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    const maxSizeMb = 10;
    const maxSizeBytes = maxSizeMb * 1024 * 1024;

    if (!allowedTypes.includes(selectedFile.type)) {
      alert("Formato file non consentito. Carica PDF, Word, JPG o PNG.");
      return;
    }

    if (selectedFile.size > maxSizeBytes) {
      alert(`File troppo grande. Dimensione massima consentita: ${maxSizeMb} MB.`);
      return;
    }

    setNuovoDocumento({
      ...nuovoDocumento,
      file: selectedFile,
    });
  };

  const openPreview = (item, fileUrl) => {
    setPreviewDocumento({
      url: fileUrl,
      title: item.title || "Documento impianto",
      type: isPdfFile(item.file_path)
        ? "pdf"
        : isImageFile(item.file_path)
        ? "image"
        : "other",
    });
  };

  const eliminaDocumento = async (item) => {
    if (isViewer) {
      mostraToast("Permesso insufficiente.", "error");
      return;
    }

    const conferma = window.confirm("Vuoi eliminare questo documento?");
    if (!conferma) return;

    try {
      const response = await authFetch(`${API}/plant-documents/${item.id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Errore eliminazione documento");
      }

      mostraToast("Documento eliminato correttamente.");

      if (onDocumentoEliminato) {
        onDocumentoEliminato(item.id);
      }
    } catch (err) {
      console.error(err);
      mostraToast("Errore durante eliminazione documento.", "error");
    }
  };

  const documentiFiltrati = documentiImpianto
    .filter((item) => {
      const search = ricercaDocumenti.toLowerCase();

      return (
        item.title?.toLowerCase().includes(search) ||
        item.document_type?.toLowerCase().includes(search) ||
        item.notes?.toLowerCase().includes(search) ||
        item.file_path?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      let valueA;
      let valueB;

      if (ordinamentoDocumenti === "nome") {
        valueA = a.title || "";
        valueB = b.title || "";
      } else if (ordinamentoDocumenti === "categoria") {
        valueA = a.document_type || "";
        valueB = b.document_type || "";
      } else {
        valueA = a.id || 0;
        valueB = b.id || 0;
      }

      if (typeof valueA === "string") {
        return ordineDocumenti === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return ordineDocumenti === "asc" ? valueA - valueB : valueB - valueA;
    });

  return (
    <div style={cardStyle}>
      {toast && (
        <div
          style={{
            position: "fixed",
            top: "24px",
            right: "24px",
            zIndex: 99999,
            minWidth: "280px",
            padding: "14px 18px",
            borderRadius: "14px",
            background: toast.type === "error" ? "#dc2626" : "#16a34a",
            color: "#fff",
            fontWeight: 700,
            boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
          }}
        >
          {toast.message}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "14px",
        }}
      >
        <h2 style={sectionTitleStyle}>
  Documenti e rapporti ({documentiImpianto.length})
</h2>

        {!isViewer && (
          <button
            className="btn btn-primary"
            onClick={() => setMostraNuovoDocumento(true)}
            style={primaryButtonStyle}
          >
            + Documento
          </button>
        )}
      </div>

      {!isViewer && mostraNuovoDocumento && (
        <div
          style={{
            ...emptyBoxStyle,
            marginBottom: "18px",
            display: "flex",
            flexDirection: "column",
            gap: "14px",
          }}
        >
          <div style={fieldWrapperStyle}>
            <div style={labelStyle}>Nome documento</div>

            <input
              placeholder="Esempio: Libretto impianto, rapporto intervento, certificazione"
              value={nuovoDocumento.title}
              onChange={(e) =>
                setNuovoDocumento({
                  ...nuovoDocumento,
                  title: e.target.value,
                })
              }
              style={inputStyle}
            />

            <div style={helpTextStyle}>
              Inserisci un nome chiaro per riconoscere facilmente il documento.
            </div>
          </div>

          <div style={fieldWrapperStyle}>
            <div style={labelStyle}>Categoria documento</div>

            <select
  value={nuovoDocumento.document_type}
  onChange={(e) =>
    setNuovoDocumento({
      ...nuovoDocumento,
      document_type: e.target.value,
    })
  }
  style={inputStyle}
>
  <option value="">Seleziona categoria</option>

  <option value="Libretto impianto">
    Libretto impianto
  </option>

  <option value="Certificazione">
    Certificazione
  </option>

  <option value="Rapporto tecnico">
    Rapporto tecnico
  </option>

  <option value="Dichiarazione conformità">
    Dichiarazione conformità
  </option>

  <option value="Manuale">
    Manuale
  </option>

  <option value="Foto impianto">
    Foto impianto
  </option>

  <option value="Altro">
    Altro
  </option>
</select>

            <div style={helpTextStyle}>
              Specifica il tipo di documento archiviato.
            </div>
          </div>

          <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "14px",
  }}
>
  <div style={fieldWrapperStyle}>
    <div style={labelStyle}>Data scadenza documento</div>

    <input
      type="date"
      value={nuovoDocumento.expiration_date || ""}
      onChange={(e) =>
        setNuovoDocumento({
          ...nuovoDocumento,
          expiration_date: e.target.value,
        })
      }
      style={inputStyle}
    />

    <div style={helpTextStyle}>
      Campo facoltativo. Se compilato, verrà creata una scadenza tecnica collegata al documento.
    </div>
  </div>

  <div style={fieldWrapperStyle}>
    <div style={labelStyle}>Periodicità documento</div>

    <input
      type="number"
      min="1"
      placeholder="Esempio: 12 per annuale"
      value={nuovoDocumento.frequency_months || ""}
      onChange={(e) =>
        setNuovoDocumento({
          ...nuovoDocumento,
          frequency_months: e.target.value,
        })
      }
      style={inputStyle}
    />

    <div style={helpTextStyle}>
      Campo facoltativo. Se inserito, la scadenza sarà rinnovabile automaticamente.
    </div>
  </div>
</div>

          <div style={fieldWrapperStyle}>
            <div style={labelStyle}>File documento</div>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                gestisciFileDocumento(e.dataTransfer.files[0]);
              }}
              style={{
                padding: "18px",
                borderRadius: "14px",
                border: isDragOver
                  ? "2px solid #0f3c75"
                  : "2px dashed #cbd5e1",
                background: isDragOver ? "#eff6ff" : "#f8fafc",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              <div style={{ fontSize: "28px", marginBottom: "8px" }}>📤</div>

              <div
                style={{
                  fontSize: "14px",
                  fontWeight: 800,
                  color: "#0f172a",
                  marginBottom: "6px",
                }}
              >
                Trascina qui il file oppure selezionalo manualmente
              </div>

              <input
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={(e) => {
                  gestisciFileDocumento(e.target.files[0]);
                  e.target.value = "";
                }}
                style={{
                  ...inputStyle,
                  marginTop: "10px",
                  background: "#fff",
                }}
              />
            </div>

            <div style={helpTextStyle}>
              Carica il documento reale dell’impianto: PDF, immagini o documenti Office.
            </div>

            {nuovoDocumento.file && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  background: "#f8fafc",
                  border: "1px solid #dbeafe",
                  fontSize: "13px",
                  color: "#0f172a",
                  fontWeight: 600,
                }}
              >
                📎 File selezionato: {nuovoDocumento.file.name}
              </div>
            )}

            {salvataggioDocumento && (
              <div style={{ marginTop: "12px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#475569",
                    marginBottom: "6px",
                  }}
                >
                  Caricamento documento...
                </div>

                <div
                  style={{
                    width: "100%",
                    height: "12px",
                    background: "#e2e8f0",
                    borderRadius: "999px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${uploadProgressDocumento}%`,
                      height: "100%",
                      background: "#0f3c75",
                      transition: "width 0.25s ease",
                    }}
                  />
                </div>

                <div
                  style={{
                    marginTop: "6px",
                    fontSize: "12px",
                    color: "#64748b",
                    fontWeight: 700,
                  }}
                >
                  {uploadProgressDocumento}%
                </div>
              </div>
            )}
          </div>

          <div style={fieldWrapperStyle}>
            <div style={labelStyle}>Note documento</div>

            <textarea
              placeholder="Esempio: Documento aggiornato, valido per verifica impianto."
              value={nuovoDocumento.notes}
              onChange={(e) =>
                setNuovoDocumento({
                  ...nuovoDocumento,
                  notes: e.target.value,
                })
              }
              rows={3}
              style={{ ...inputStyle, resize: "vertical" }}
            />

            <div style={helpTextStyle}>
              Aggiungi eventuali informazioni utili sul contenuto o sulla validità del documento.
            </div>
          </div>

          {renderActions(
            salvaNuovoDocumento,
            () => {
              if (!salvataggioDocumento) {
                setMostraNuovoDocumento(false);
              }
            },
            salvataggioDocumento ? "Salvataggio..." : "Salva documento"
          )}
        </div>
      )}

      <div style={{ marginBottom: "18px" }}>
        <input
          type="text"
          placeholder="Cerca documento..."
          value={ricercaDocumenti}
          onChange={(e) => setRicercaDocumenti(e.target.value)}
          style={{
            ...inputStyle,
            background: "#fff",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "18px",
          flexWrap: "wrap",
        }}
      >
        <select
          value={ordinamentoDocumenti}
          onChange={(e) => setOrdinamentoDocumenti(e.target.value)}
          style={{
            ...inputStyle,
            width: "220px",
            background: "#fff",
          }}
        >
          <option value="recenti">Più recenti</option>
          <option value="nome">Nome documento</option>
          <option value="categoria">Categoria</option>
        </select>

        <select
          value={ordineDocumenti}
          onChange={(e) => setOrdineDocumenti(e.target.value)}
          style={{
            ...inputStyle,
            width: "180px",
            background: "#fff",
          }}
        >
          <option value="desc">Decrescente</option>
          <option value="asc">Crescente</option>
        </select>
      </div>

      {documentiFiltrati.length === 0 ? (
  <div style={emptyBoxStyle}>Nessun documento caricato.</div>
) : (
  <div
    style={{
      maxHeight: documentiFiltrati.length > 3 ? "650px" : "none",
      overflowY: documentiFiltrati.length > 3 ? "auto" : "visible",
      paddingRight: documentiFiltrati.length > 3 ? "6px" : 0,
    }}
  >
    {documentiFiltrati.map((item) => {
          const fileUrl = getFileUrl(item.file_path);
          const fileLabel = getFileLabel(item.file_path);

          return (
            <div
              key={item.id}
              style={{
                ...emptyBoxStyle,
                marginBottom: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                border: "1px solid #e2e8f0",
                background: "#ffffff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "14px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontWeight: 900,
                      color: "#0f172a",
                      marginBottom: "8px",
                      fontSize: "15px",
                    }}
                  >
                    {getDocumentIcon(item.file_path)}{" "}
                    {item.title || "Documento impianto"}
                  </div>

                  <div
  style={{
    fontSize: "12px",
    color: "#64748b",
    marginBottom: "8px",
  }}
>
  Caricato il {item.created_at ? new Date(item.created_at).toLocaleDateString("it-IT") : "—"}
</div>

                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={getCategoryBadgeStyle()}>
                      {item.document_type || "Documento tecnico"}
                    </span>

                    <span style={getFileTypeBadgeStyle()}>
                      {getFileTypeLabel(item.file_path)}
                    </span>
                  </div>
                </div>

                {fileUrl && (
  <div
    style={{
  display: "flex",
  gap: "10px",
  alignItems: "flex-start",
  flexWrap: "wrap",
  marginTop: "8px",
}}
  >
    <button
      onClick={() => openPreview(item, fileUrl)}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#f8fafc";
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow =
          "0 4px 10px rgba(15,23,42,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#ffffff";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
      style={{
        padding: "10px 16px",
        borderRadius: "10px",
        border: "1px solid #cbd5e1",
        background: "#ffffff",
        cursor: "pointer",
        color: "#173a63",
        fontWeight: 700,
        fontSize: "14px",
        transition: "all 0.2s ease",
      }}
    >
      👁 Anteprima
    </button>

    <a
      href={fileUrl}
      target="_blank"
      rel="noreferrer"
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "#1e4a7a";
        e.currentTarget.style.transform = "translateY(-1px)";
        e.currentTarget.style.boxShadow =
          "0 4px 12px rgba(23,58,99,0.25)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "#173a63";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
      style={{
        padding: "10px 16px",
        borderRadius: "10px",
        background: "#173a63",
        color: "#fff",
        textDecoration: "none",
        fontWeight: 700,
        fontSize: "14px",
        transition: "all 0.2s ease",
      }}
    >
      ⬇ Scarica
    </a>

    {!isViewer && (
      <button
        onClick={() => eliminaDocumento(item)}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#b91c1c";
          e.currentTarget.style.transform = "translateY(-1px)";
          e.currentTarget.style.boxShadow =
            "0 4px 12px rgba(185,28,28,0.25)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#dc2626";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }}
        style={{
          padding: "10px 16px",
          borderRadius: "10px",
          border: "none",
          background: "#dc2626",
          color: "#fff",
          cursor: "pointer",
          fontWeight: 700,
          fontSize: "14px",
          transition: "all 0.2s ease",
        }}
      >
        🗑 Elimina
      </button>
    )}
  </div>
)}
              </div>

              {item.notes && (
  <div
    style={{
      fontSize: "14px",
      color: "#334155",
      lineHeight: 1.45,
    }}
  >
    <strong>Note documento:</strong> {item.notes}
  </div>
)}
                        </div>
          );
        })}
  </div>
)}

      {previewDocumento && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.82)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "30px",
          }}
        >
          <div
            style={{
              width: "95%",
              height: "95%",
              background: "#fff",
              borderRadius: "18px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 25px 60px rgba(0,0,0,0.35)",
            }}
          >
            <div
              style={{
                padding: "16px 22px",
                borderBottom: "1px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f8fafc",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "18px",
                    fontWeight: 800,
                    color: "#0f172a",
                  }}
                >
                  {previewDocumento.title || "Preview documento"}
                </div>

                <div
                  style={{
                    fontSize: "13px",
                    color: "#64748b",
                    marginTop: "3px",
                  }}
                >
                  Anteprima documento impianto
                </div>
              </div>

              <div style={{ display: "flex", gap: "10px" }}>
                <a
                  href={previewDocumento.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    background: "#0f3c75",
                    color: "#fff",
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                >
                  Scarica
                </a>

                <button
                  onClick={() => setPreviewDocumento(null)}
                  style={{
                    padding: "10px 14px",
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    background: "#fff",
                    cursor: "pointer",
                    fontWeight: 700,
                  }}
                >
                  Chiudi
                </button>
              </div>
            </div>

            <div style={{ flex: 1, background: "#e2e8f0" }}>
              {previewDocumento.type === "pdf" && (
                <iframe
                  src={previewDocumento.url}
                  title="PDF Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                />
              )}

              {previewDocumento.type === "image" && (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    overflow: "auto",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    background: "#0f172a",
                  }}
                >
                  <img
                    src={previewDocumento.url}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}

              {previewDocumento.type === "other" && (
                <div
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "18px",
                    background: "#f8fafc",
                  }}
                >
                  <div style={{ fontSize: "70px" }}>📄</div>

                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: 800,
                      color: "#0f172a",
                    }}
                  >
                    Anteprima non disponibile
                  </div>

                    <a
                    href={previewDocumento.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      padding: "12px 18px",
                      borderRadius: "10px",
                      background: "#0f3c75",
                      color: "#fff",
                      textDecoration: "none",
                      fontWeight: 700,
                    }}
                  >
                    Apri documento
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      </div>
  );
}

export default PlantDocumentsPanel;